import React, { useState } from 'react';
import { useDeveloperWorkbench } from './DeveloperWorkbench.hook';
import { DeveloperWorkbenchButton } from './DeveloperWorkbenchButton';
import { ToolsTab } from './ToolsTab';
import { OverridesTab } from './OverridesTab';

interface DeveloperWorkbenchProps {
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
}

export const DeveloperWorkbench: React.FC<DeveloperWorkbenchProps> = ({
  isOpen: propIsOpen,
  setIsOpen: propSetIsOpen,
}) => {
  const {
    isDevMode,
    isOpen: hookIsOpen,
    setIsOpen: hookSetIsOpen,
    overrides,
    handleInputChange,
    handleSave,
    handleReset,
    configs,
  } = useDeveloperWorkbench();

  const [activeTab, setActiveTab] = useState<'overrides' | 'tools'>('overrides');

  // Determine whether to use controlled props or hook state
  const isOpen = propIsOpen !== undefined ? propIsOpen : hookIsOpen;
  const setIsOpen = propSetIsOpen !== undefined ? propSetIsOpen : hookSetIsOpen;

  // If developer mode URL parameter is not set, don't render anything
  if (!isDevMode) {
    return null;
  }

  return (
    <>
      {/* Floating Button - Only show if not controlled externally */}
      {propIsOpen === undefined && <DeveloperWorkbenchButton onClick={() => setIsOpen(true)} />}

      {/* Modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="w-full max-w-2xl bg-bg-deep rounded-3xl border border-border-color shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-border-color shrink-0 flex justify-between items-start gap-4">
              <div>
                <h3 className="text-base md:text-lg font-bold text-text-primary flex items-center gap-2 mb-1.5">
                  <i className="fa-solid fa-screwdriver-wrench text-primary-accent"></i> 
                  <span>Developer Workbench</span>
                </h3>
                <p className="text-xs md:text-sm text-text-secondary leading-relaxed">
                  Configure micro frontend URL overrides. When saved, local storage configurations are read by loaders, allowing you to point MFEs to your local dev servers (e.g. <code>localhost</code>).
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-text-secondary hover:text-text-primary cursor-pointer text-base bg-transparent border-none transition-colors outline-none"
                title="Close"
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            {/* Tab Bar */}
            <div className="px-6 py-3 border-b border-border-color shrink-0 flex gap-2.5">
              <button
                onClick={() => setActiveTab('overrides')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-colors border ${
                  activeTab === 'overrides'
                    ? 'bg-primary-accent border-primary-accent text-bg-deep dark:text-black'
                    : 'bg-white/5 border-border-color text-text-secondary hover:text-text-primary hover:bg-white/10'
                }`}
              >
                <i className="fa-solid fa-network-wired mr-1.5"></i>
                MFE Overrides
              </button>
              <button
                onClick={() => setActiveTab('tools')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-colors border ${
                  activeTab === 'tools'
                    ? 'bg-primary-accent border-primary-accent text-bg-deep dark:text-black'
                    : 'bg-white/5 border-border-color text-text-secondary hover:text-text-primary hover:bg-white/10'
                }`}
              >
                <i className="fa-solid fa-toolbox mr-1.5"></i>
                Developer Tools
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex flex-col gap-6 grow">
              {activeTab === 'tools' && <ToolsTab onClose={() => setIsOpen(false)} />}
              {activeTab === 'overrides' && (
                <OverridesTab 
                  configs={configs} 
                  overrides={overrides} 
                  onInputChange={handleInputChange} 
                />
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-border-color shrink-0 flex flex-wrap gap-3 items-center justify-end">
              {activeTab === 'overrides' ? (
                <>
                  <button 
                    onClick={handleReset} 
                    className="bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/20 px-4 py-2.5 rounded-xl text-xs md:text-sm font-bold cursor-pointer transition-colors flex items-center gap-2"
                  >
                    <i className="fa-solid fa-arrow-rotate-left"></i>
                    Reset All Defaults
                  </button>
                  <div className="flex-grow" />
                  <button 
                    onClick={() => setIsOpen(false)} 
                    className="bg-white/5 hover:bg-white/10 text-text-secondary border border-border-color px-4 py-2.5 rounded-xl text-xs md:text-sm font-bold cursor-pointer transition-colors flex items-center gap-2"
                  >
                    <i className="fa-solid fa-xmark"></i>
                    Cancel
                  </button>
                  <button 
                    onClick={handleSave} 
                    className="bg-primary-accent hover:opacity-90 text-bg-deep dark:text-black px-4 py-2.5 rounded-xl text-xs md:text-sm font-bold cursor-pointer transition-colors flex items-center gap-2"
                  >
                    <i className="fa-solid fa-floppy-disk"></i>
                    Save & Apply Overrides
                  </button>
                </>
              ) : (
                <button 
                  onClick={() => setIsOpen(false)} 
                  className="bg-white/5 hover:bg-white/10 text-text-secondary border border-border-color px-4 py-2.5 rounded-xl text-xs md:text-sm font-bold cursor-pointer transition-colors flex items-center gap-2"
                >
                  <i className="fa-solid fa-xmark"></i>
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DeveloperWorkbench;
